import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, deals, stores } from '../data/mockData';
import { ShoppingCart, Ticket, ChevronRight, Copy, ExternalLink, Clock, Eye, ThumbsUp, Timer, Flame, Heart } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DealDialog from '../components/DealDialog';
import DealStatsInteractive from '../components/DealStatsInteractive';
import { useAuth } from '../context/AuthContext';

const CategoryPage = () => {
  const { slug } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const categoryDeals = deals.filter((d) => d.category === slug);
  const [revealedCoupons, setRevealedCoupons] = useState({});
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 12, minutes: 45, seconds: 30 });
  const [loadingFavorite, setLoadingFavorite] = useState(null);
  
  const { isAuthenticated, isFavorite, addFavorite, removeFavorite } = useAuth();

  const handleToggleFavorite = async (e, dealId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    setLoadingFavorite(dealId);
    try {
      if (isFavorite(dealId)) {
        await removeFavorite(dealId);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(dealId);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingFavorite(null);
    }
  };

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { hours, minutes, seconds };
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  // Get Deal of the Day - highest discount deal
  const dealOfTheDay = useMemo(() => {
    if (categoryDeals.length === 0) return null;
    return categoryDeals.reduce((max, deal) => deal.discount > max.discount ? deal : max, categoryDeals[0]);
  }, [categoryDeals]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const getStoreName = (storeSlug) => {
    const store = stores.find(s => s.slug === storeSlug);
    return store?.name || storeSlug;
  };

  const handleBuyNow = (deal) => {
    setSelectedDeal({ ...deal, storeName: getStoreName(deal.store) });
    setDialogOpen(true);
  };

  const handleRevealCoupon = async (deal) => {
    setRevealedCoupons(prev => ({ ...prev, [deal.id]: true }));
    const copied = await copyToClipboard(deal.couponCode);
    toast.success(
      <div className="flex flex-col">
        <span className="font-semibold flex items-center gap-2">
          <Copy size={16} /> Coupon Code {copied ? 'Copied!' : 'Revealed!'}
        </span>
        <span className="text-lg font-mono bg-gray-100 px-2 py-1 rounded mt-1">{deal.couponCode}</span>
      </div>,
      { duration: 5000 }
    );
  };

  const handleGoToStore = (deal) => {
    setSelectedDeal({ ...deal, storeName: getStoreName(deal.store) });
    setDialogOpen(true);
  };

  const getBadgeColor = (badge) => {
    if (!badge) return 'bg-gray-500';
    if (badge.includes('Best seller')) return 'bg-gray-800';
    if (badge.includes('Hot') || badge.includes('Mega')) return 'bg-orange-600';
    if (badge.includes('Best price') || badge.includes('Best value')) return 'bg-blue-500';
    if (badge.includes('Clearance')) return 'bg-purple-500';
    if (badge.includes('Limited')) return 'bg-yellow-600';
    if (badge.includes('Trending')) return 'bg-pink-500';
    return 'bg-gray-500';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-gray-800">Home</Link>
              <ChevronRight size={14} className="mx-2" />
              <span className="text-gray-800 font-medium">{category?.name || 'Category'}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Category Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">{category?.name || 'Category'}</h1>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Deal of the Day Section */}
              {dealOfTheDay && (
                <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
                  <div className="bg-[#000000] px-4 py-3 flex items-center gap-2">
                    <Flame className="text-yellow-400" size={20} />
                    <span className="text-white font-bold">Deal of the Day</span>
                    <div className="ml-auto flex items-center gap-2 text-white text-sm font-mono">
                      <Timer size={16} className="text-yellow-400" />
                      <span className="bg-white/20 px-2 py-0.5 rounded">{formatTime(timeLeft.hours)}</span>
                      <span>:</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded">{formatTime(timeLeft.minutes)}</span>
                      <span>:</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded">{formatTime(timeLeft.seconds)}</span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Image */}
                      <div className="md:w-64 flex-shrink-0">
                        <div className="relative">
                          <img
                            src={dealOfTheDay.image}
                            alt={dealOfTheDay.title}
                            className="w-full h-48 md:h-64 object-contain bg-gray-50 rounded-lg"
                          />
                          <div className="absolute top-2 right-2 bg-black text-white text-lg font-bold px-3 py-1 rounded">
                            -{dealOfTheDay.discount}%
                          </div>
                          {/* Favorite Button */}
                          <button
                            onClick={(e) => handleToggleFavorite(e, dealOfTheDay.id)}
                            disabled={loadingFavorite === dealOfTheDay.id}
                            className={`absolute top-2 left-2 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                              isFavorite(dealOfTheDay.id) 
                                ? 'bg-black text-white' 
                                : 'bg-white text-gray-400 hover:text-black shadow-md'
                            }`}
                            data-testid={`favorite-btn-dotd-${dealOfTheDay.id}`}
                          >
                            {loadingFavorite === dealOfTheDay.id ? (
                              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Heart size={20} fill={isFavorite(dealOfTheDay.id) ? 'currentColor' : 'none'} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                          {dealOfTheDay.title}
                        </h2>
                        
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-3xl font-bold text-gray-800">${dealOfTheDay.salePrice?.toFixed(2)}</span>
                          <span className="text-xl text-gray-400 line-through">${dealOfTheDay.originalPrice?.toFixed(2)}</span>
                        </div>

                        <p className="text-gray-800 font-medium mb-3">{dealOfTheDay.cashback}</p>
                        <p className="text-gray-600 mb-4">{dealOfTheDay.description}</p>

                        {/* Interactive Stats */}
                        <div className="flex items-center gap-4 mb-4">
                          <DealStatsInteractive 
                            dealId={dealOfTheDay.id} 
                            initialViews={dealOfTheDay.views} 
                          />
                          <Link to={`/store/${dealOfTheDay.store}`} className="text-gray-800 hover:underline text-xs">
                            {getStoreName(dealOfTheDay.store)}
                          </Link>
                        </div>

                        <button
                          onClick={() => handleBuyNow(dealOfTheDay)}
                          className="bg-[#000000] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#000000] transition-colors flex items-center gap-2"
                        >
                          <ShoppingCart size={18} />
                          Buy It Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Deals List */}
              <div className="space-y-4">
                {categoryDeals.filter(d => d.id !== dealOfTheDay?.id).map((deal) => (
                  <div
                    key={deal.id}
                    className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Vote/Discount Badge */}
                      <div className="md:w-16 bg-gray-50 flex md:flex-col items-center justify-center p-3 md:p-4 border-b md:border-b-0 md:border-r">
                        <span className="text-2xl md:text-3xl font-bold text-gray-800">{deal.discount}</span>
                        <span className="text-xs text-gray-500 ml-1 md:ml-0">%</span>
                      </div>

                      {/* Image */}
                      <div className="md:w-40 p-4 flex items-center justify-center bg-gray-50 relative">
                        <img
                          src={deal.image}
                          alt={deal.title}
                          className="w-24 h-24 md:w-32 md:h-32 object-contain"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop';
                          }}
                        />
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => handleToggleFavorite(e, deal.id)}
                          disabled={loadingFavorite === deal.id}
                          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            isFavorite(deal.id) 
                              ? 'bg-black text-white' 
                              : 'bg-white text-gray-400 hover:text-black shadow-sm'
                          }`}
                          data-testid={`favorite-btn-${deal.id}`}
                        >
                          {loadingFavorite === deal.id ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Heart size={16} fill={isFavorite(deal.id) ? 'currentColor' : 'none'} />
                          )}
                        </button>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4">
                        {/* Badges */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {deal.badge && (
                            <span className={`${getBadgeColor(deal.badge)} text-white text-xs px-2 py-0.5 rounded`}>
                              {deal.badge}
                            </span>
                          )}
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <Clock size={12} />
                            1 year ago
                          </span>
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <Eye size={12} />
                            {deal.views}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-gray-800 hover:text-gray-800 mb-2 line-clamp-2">
                          {deal.title}
                        </h3>

                        {/* Price */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xl font-bold text-gray-800">${deal.salePrice?.toFixed(2)}</span>
                          <span className="text-gray-400 line-through">${deal.originalPrice?.toFixed(2)}</span>
                        </div>

                        {/* Cashback & Store */}
                        <p className="text-gray-800 text-sm mb-2">{deal.cashback}</p>
                        <div className="flex items-center gap-2 mb-3">
                          <Link to={`/store/${deal.store}`} className="text-sm text-gray-500 hover:text-gray-800">
                            {getStoreName(deal.store)}
                          </Link>
                        </div>

                        {/* Button */}
                        <div className="flex flex-col sm:flex-row gap-2">
                          {deal.buttonType === 'coupon' ? (
                            revealedCoupons[deal.id] ? (
                              <>
                                <div className="flex-1 bg-gray-100 border-2 border-dashed border-[#000000] text-gray-800 py-2.5 px-4 rounded font-mono text-center font-bold">
                                  {deal.couponCode}
                                </div>
                                <button 
                                  onClick={() => handleGoToStore(deal)}
                                  className="bg-[#000000] text-white py-2.5 px-6 rounded font-medium text-sm hover:bg-[#000000] transition-colors flex items-center justify-center gap-2"
                                >
                                  <ExternalLink size={16} />
                                  Go to Store
                                </button>
                              </>
                            ) : (
                              <button 
                                onClick={() => handleRevealCoupon(deal)}
                                className="flex-1 bg-orange-500 text-white py-2.5 px-6 rounded font-medium text-sm hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                              >
                                <Ticket size={16} />
                                Reveal coupon
                              </button>
                            )
                          ) : (
                            <button 
                              onClick={() => handleBuyNow(deal)}
                              className="flex-1 bg-[#000000] text-white py-2.5 px-6 rounded font-medium text-sm hover:bg-[#000000] transition-colors flex items-center justify-center gap-2"
                            >
                              <ShoppingCart size={16} />
                              Buy It Now
                            </button>
                          )}
                        </div>

                        {/* Interactive Stats */}
                        <div className="mt-3 pt-3 border-t">
                          <DealStatsInteractive dealId={deal.id} initialViews={deal.views} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {categoryDeals.length === 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <p className="text-gray-500">No deals available in this category yet.</p>
                    <Link to="/" className="text-gray-800 hover:underline mt-2 inline-block">
                      Browse all deals
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Categories Widget */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-4 sticky top-20">
                <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b">Categories</h3>
                <ul className="space-y-1">
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`block px-2 py-1.5 rounded text-sm transition-colors ${
                          cat.slug === slug
                            ? 'bg-[#000000] text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Stores Widget */}
                <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b mt-6">Popular Stores</h3>
                <div className="flex flex-wrap gap-1">
                  {stores.slice(0, 10).map((store) => (
                    <Link
                      key={store.id}
                      to={`/store/${store.slug}`}
                      className="text-xs text-gray-800 hover:underline"
                    >
                      {store.name},
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Deal Dialog */}
      <DealDialog
        deal={selectedDeal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        storeName={selectedDeal?.storeName}
      />
    </div>
  );
};

export default CategoryPage;
